import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { submitContactMessage, getCompanyInfo } from '../api/api';

export const Contact = () => {
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false,
    website: '' // champ piège : invisible pour l'utilisateur, rempli par les robots
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const data = await getCompanyInfo();
        setCompany(data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };

    fetchCompany();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContactMessage(formData);
      toast.success('Message envoyé ! Nous vous répondrons rapidement.', {
        duration: 5000
      });
      setFormData({ name: '', email: '', phone: '', message: '', consent: false, website: '' });
    } catch (error) {
      console.error('Error submitting contact message:', error);
      toast.error(
        typeof error.response?.data?.detail === 'string'
          ? error.response.data.detail
          : error.response?.status === 429
            ? 'Trop de messages envoyés. Réessayez dans quelques minutes.'
            : 'Une erreur est survenue. Veuillez réessayer.', {
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!company) {
    return null;
  }

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-gray-900 mb-4">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Une question ? Un projet ? N'hésitez pas à nous contacter
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Nos coordonnées
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Notre équipe est à votre écoute pour répondre à toutes vos questions et établir un devis personnalisé adapté à vos besoins.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              <Card className="border-0 bg-gradient-to-br from-blue-50 to-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-blue-600 p-3 rounded-xl">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Téléphone</div>
                    <a href={`tel:${company.phone}`} className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                      {company.phone}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-gradient-to-br from-green-50 to-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-green-600 to-green-700 p-3 rounded-xl">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Email</div>
                    <a href={`mailto:${company.email}`} className="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors break-all">
                      {company.email}
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-3 rounded-xl">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Adresse</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {company.address}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-xl h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d81308.87374975936!2d5.494153!3d50.633333!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c0f74b8eacfcfb%3A0x40099ab2f4d6300!2sLi%C3%A8ge%2C%20Belgium!5e0!3m2!1sen!2sus!4v1635000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="NETTO+ Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Envoyez-nous un message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="contact-name" className="text-gray-700 font-semibold">
                    Nom *
                  </Label>
                  <Input
                    id="contact-name"
                    type="text"
                    placeholder="Votre nom"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                    className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email" className="text-gray-700 font-semibold">
                    Email *
                  </Label>
                  <Input
                    id="contact-email"
                    type="email"
                    placeholder="votre.email@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-phone" className="text-gray-700 font-semibold">
                    Téléphone
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    placeholder="+32 4 123 45 67"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="h-12 border-gray-200 focus:border-blue-600 focus:ring-blue-600"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-gray-700 font-semibold">
                    Message *
                  </Label>
                  <Textarea
                    id="contact-message"
                    placeholder="Votre message..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    required
                    rows={5}
                    className="border-gray-200 focus:border-blue-600 focus:ring-blue-600 resize-none"
                  />
                </div>

                {/* Champ piège : masqué à l'écran et aux lecteurs d'écran. */}
                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <label htmlFor="website-ct">Ne pas remplir</label>
                  <input
                    id="website-ct"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                  />
                </div>

                {/* Consentement RGPD */}
                <div className="flex items-start gap-3">
                  <input
                    id="consent-ct"
                    type="checkbox"
                    required
                    checked={formData.consent}
                    onChange={(e) => handleChange('consent', e.target.checked)}
                    className="mt-1 w-4 h-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                  />
                  <Label htmlFor="consent-ct" className="text-sm text-gray-600 font-normal leading-relaxed">
                    J'accepte que mes données soient utilisées pour répondre à mon message.
                    Consultez notre{' '}
                    <Link to="/confidentialite" target="_blank" className="text-blue-600 underline hover:text-blue-700">
                      politique de confidentialité
                    </Link>
                    . *
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-blue-600/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-600/40 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer le message
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
